import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError
} from '@reduxjs/toolkit/query/react';

/**
 * All server access goes through RTK Query. Components never call fetch
 * directly, so caching, loading state and invalidation stay in one place.
 */

/** The CSRF cookie is readable by design; mutations must echo it in a header. */
const readCsrfCookie = (): string => {
  const match = document.cookie.match(/(?:^|;\s*)cw_csrf=([^;]+)/);
  return match?.[1] ?? '';
};

export interface Paginated<T> {
  data: T[];
  meta: { page: number; limit: number; total: number; pages: number };
}

export interface SessionResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    isPlatformAdmin: boolean;
    emailVerified: boolean;
  };
  role: string | null;
  organization: { id: string; name: string; status: string } | null;
  organizations: { id: string; name: string; role: string }[];
}

export interface DashboardResponse {
  stats: {
    callsToday: number;
    answeredCalls: number;
    missedCalls: number;
    transferredCalls: number;
    phoneOrders: number;
    phoneOrderRevenueCents: number;
    averageOrderValueCents: number;
    averageCallDurationSeconds: number;
    minutesUsed: number;
  };
  usage: {
    includedMinutes: number;
    usedMinutes: number;
    remainingMinutes: number;
    usagePercent: number;
    overageMinutes: number;
    overageCostCents: number;
  };
}

export interface CallRecord {
  _id: string;
  startedAt: string;
  callerNumber: string;
  destinationNumber: string;
  durationSeconds: number;
  status: string;
  currentState: string;
  transferred: boolean;
  /** OUTBOUND = a callback the agent placed to this customer. */
  direction?: 'INBOUND' | 'OUTBOUND';
  orderId?: string;
  intentHistory: { intent: string; confidence: number; transcript: string }[];
}

export interface OrderRecord {
  _id: string;
  orderNumber: string;
  customerName?: string;
  customerPhone?: string;
  fulfillment: string;
  status: string;
  totalCents: number;
  createdAt: string;
  externalPosOrderId?: string;
  /** Set once a customer payment against the order was verified. */
  paidAt?: string;
}

export interface MenuItemRecord {
  _id: string;
  name: string;
  description?: string;
  categoryId?: string;
  priceCents: number;
  available: boolean;
  aliases: string[];
  vegetarian: boolean;
  vegan?: boolean;
  halal?: boolean;
  glutenFree?: boolean;
  /** Set when the item came from a POS sync rather than being typed here. */
  externalPosId?: string;
}

export interface ModifierGroupRecord {
  _id: string;
  name: string;
  prompt?: string;
  required: boolean;
  minSelections: number;
  maxSelections: number;
  externalPosId?: string;
}

export interface ModifierRecord {
  _id: string;
  groupId: string;
  name: string;
  priceCents: number;
  available: boolean;
  externalPosId?: string;
}

export interface MenuCategoryRecord {
  _id: string;
  name: string;
  description?: string;
  sortOrder: number;
  active: boolean;
  /** Present when the category came from a POS sync. */
  externalPosId?: string;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: '/api',
  credentials: 'include',
  prepareHeaders: (headers, { type }) => {
    if (type === 'mutation') headers.set('X-CSRF-Token', readCsrfCookie());
    return headers;
  }
});

/**
 * One refresh at a time. Several queries expire together when a screen loads,
 * and each would otherwise race to rotate the same refresh cookie; the losers
 * would then present a token the server has already replaced.
 */
let refreshing: Promise<boolean> | null = null;

export const refreshSession = (): Promise<boolean> => {
  refreshing ??= fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRF-Token': readCsrfCookie() }
  })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
};

/**
 * The access cookie lives fifteen minutes; the refresh cookie thirty days.
 * A 401 therefore usually means "rotate and retry", not "signed out" — an
 * operator mid-shift must not be bounced to the login page every quarter hour.
 * Only a refresh the server itself rejects ends the session.
 */
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  apiCtx,
  extraOptions
) => {
  let result = await rawBaseQuery(args, apiCtx, extraOptions);

  const url = typeof args === 'string' ? args : args.url;
  const isAuthCall = url.startsWith('/auth/login') || url.startsWith('/auth/refresh');

  if (result.error?.status === 401 && !isAuthCall) {
    const refreshed = await refreshSession();
    if (refreshed) result = await rawBaseQuery(args, apiCtx, extraOptions);
  }

  return result;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Session',
    'Dashboard',
    'Call',
    'Order',
    'Menu',
    'Location',
    'Restaurant',
    'Faq',
    'Prompt',
    'Pronunciation',
    'Phone',
    'Onboarding',
    'Pos',
    'Team',
    'Billing',
    'Usage',
    'Admin',
    'Notification'
  ],
  endpoints: (builder) => ({
    getSession: builder.query<SessionResponse, void>({
      query: () => '/auth/me',
      providesTags: ['Session']
    }),
    login: builder.mutation<SessionResponse, { email: string; password: string }>({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
      invalidatesTags: ['Session']
    }),
    register: builder.mutation<
      SessionResponse,
      {
        firstName: string;
        lastName: string;
        email: string;
        password: string;
        companyName: string;
      }
    >({
      query: (body) => ({ url: '/auth/register', method: 'POST', body }),
      invalidatesTags: ['Session']
    }),
    logout: builder.mutation<void, void>({
      query: () => ({ url: '/auth/logout', method: 'POST' }),
      invalidatesTags: ['Session']
    }),

    getDashboard: builder.query<DashboardResponse, { locationId?: string } | void>({
      query: (params) =>
        params?.locationId
          ? `/analytics/dashboard?locationId=${params.locationId}`
          : '/analytics/dashboard',
      providesTags: ['Dashboard']
    }),
    getTrends: builder.query<
      { trend: { _id: string; calls: number; orders: number; minutes: number }[] },
      { days?: number } | void
    >({
      query: (params) => `/analytics/trends?days=${params?.days ?? 30}`
    }),

    getCalls: builder.query<Paginated<CallRecord>, Record<string, string | number> | void>({
      query: (params) => ({ url: '/calls', params: params ?? {} }),
      providesTags: ['Call']
    }),
    getCall: builder.query<CallRecord & { transcript: unknown[] }, string>({
      query: (id) => `/calls/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Call', id }]
    }),

    getCallbackAvailability: builder.query<{ available: boolean }, void>({
      query: () => '/calls/callback-availability'
    }),
    callBack: builder.mutation<{ callId: string; from: string; to: string }, string>({
      query: (id) => ({ url: `/calls/${id}/callback`, method: 'POST' }),
      invalidatesTags: ['Call']
    }),

    getOrders: builder.query<Paginated<OrderRecord>, Record<string, string | number> | void>({
      query: (params) => ({ url: '/orders', params: params ?? {} }),
      providesTags: ['Order']
    }),
    retryPosSync: builder.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/orders/${id}/retry-pos`, method: 'POST' }),
      invalidatesTags: ['Order']
    }),

    getLocations: builder.query<{ data: { _id: string; name: string }[] }, void>({
      query: () => '/locations',
      providesTags: ['Location']
    }),
    getMenu: builder.query<
      {
        items: MenuItemRecord[];
        categories: MenuCategoryRecord[];
        modifierGroups: ModifierGroupRecord[];
        modifiers: ModifierRecord[];
      },
      string
    >({
      query: (locationId) => `/menu?locationId=${locationId}`,
      providesTags: ['Menu']
    }),
    setItemAvailability: builder.mutation<
      MenuItemRecord,
      { id: string; available: boolean }
    >({
      query: ({ id, available }) => ({
        url: `/menu/items/${id}/availability`,
        method: 'PATCH',
        body: { available }
      }),
      invalidatesTags: ['Menu']
    }),
    /* ------------------------------------------------------- menu editing */
    createMenuItem: builder.mutation<
      MenuItemRecord,
      { locationId: string } & Partial<Omit<MenuItemRecord, '_id'>>
    >({
      query: (body) => ({ url: '/menu/items', method: 'POST', body }),
      invalidatesTags: ['Menu']
    }),
    updateMenuItem: builder.mutation<
      MenuItemRecord,
      { id: string } & Partial<Omit<MenuItemRecord, '_id'>>
    >({
      query: ({ id, ...body }) => ({ url: `/menu/items/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Menu']
    }),
    deleteMenuItem: builder.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/menu/items/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Menu']
    }),
    createMenuCategory: builder.mutation<
      MenuCategoryRecord,
      { locationId: string; name: string; description?: string; sortOrder?: number }
    >({
      query: (body) => ({ url: '/menu/categories', method: 'POST', body }),
      invalidatesTags: ['Menu']
    }),
    createModifierGroup: builder.mutation<
      { _id: string; name: string },
      {
        locationId: string;
        name: string;
        required?: boolean;
        minSelections?: number;
        maxSelections?: number;
      }
    >({
      query: (body) => ({ url: '/menu/modifier-groups', method: 'POST', body }),
      invalidatesTags: ['Menu']
    }),
    createModifier: builder.mutation<
      { _id: string; name: string },
      { groupId: string; name: string; priceCents: number; available?: boolean }
    >({
      query: (body) => ({ url: '/menu/modifiers', method: 'POST', body }),
      invalidatesTags: ['Menu']
    }),
    createMenuAlias: builder.mutation<
      { _id: string },
      { locationId: string; menuItemId: string; phrase: string }
    >({
      query: (body) => ({ url: '/menu/aliases', method: 'POST', body }),
      invalidatesTags: ['Menu']
    }),
    deleteMenuAlias: builder.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/menu/aliases/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Menu']
    }),

    updateMenuCategory: builder.mutation<
      MenuCategoryRecord,
      { id: string } & Partial<Omit<MenuCategoryRecord, '_id'>>
    >({
      query: ({ id, ...body }) => ({ url: `/menu/categories/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Menu']
    }),
    deleteMenuCategory: builder.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/menu/categories/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Menu']
    }),
    reorderMenuCategories: builder.mutation<{ ok: boolean }, { order: string[] }>({
      query: (body) => ({ url: '/menu/categories/reorder', method: 'POST', body }),
      invalidatesTags: ['Menu']
    }),
    updateModifierGroup: builder.mutation<
      ModifierGroupRecord,
      { id: string } & Partial<Omit<ModifierGroupRecord, '_id'>>
    >({
      query: ({ id, ...body }) => ({ url: `/menu/modifier-groups/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Menu']
    }),
    deleteModifierGroup: builder.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/menu/modifier-groups/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Menu']
    }),
    updateModifier: builder.mutation<
      ModifierRecord,
      { id: string } & Partial<Omit<ModifierRecord, '_id'>>
    >({
      query: ({ id, ...body }) => ({ url: `/menu/modifiers/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Menu']
    }),
    deleteModifier: builder.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/menu/modifiers/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Menu']
    }),
    setItemModifierGroups: builder.mutation<
      MenuItemRecord,
      { id: string; modifierGroupIds: string[] }
    >({
      query: ({ id, modifierGroupIds }) => ({
        url: `/menu/items/${id}/modifier-groups`,
        method: 'PUT',
        body: { modifierGroupIds }
      }),
      invalidatesTags: ['Menu']
    }),

    testMenuMatch: builder.mutation<
      {
        matches: {
          name: string;
          quantity: number;
          confidence: number;
          method: string;
          wouldAdd: boolean;
          wouldConfirm: boolean;
        }[];
      },
      { phrase: string; locationId: string }
    >({
      query: (body) => ({ url: '/menu/test', method: 'POST', body })
    }),

    getNotifications: builder.query<
      { data: { _id: string; title: string; body: string; severity: string }[]; unread: number },
      void
    >({
      query: () => '/notifications',
      providesTags: ['Notification']
    })
  })
});

export const {
  useGetSessionQuery,
  useLoginMutation,
  useRegisterMutation,
  useLogoutMutation,
  useGetDashboardQuery,
  useGetTrendsQuery,
  useGetCallsQuery,
  useGetCallQuery,
  useGetCallbackAvailabilityQuery,
  useCallBackMutation,
  useGetOrdersQuery,
  useRetryPosSyncMutation,
  useGetLocationsQuery,
  useGetMenuQuery,
  useSetItemAvailabilityMutation,
  useCreateMenuItemMutation,
  useUpdateMenuItemMutation,
  useDeleteMenuItemMutation,
  useCreateMenuCategoryMutation,
  useCreateModifierGroupMutation,
  useCreateModifierMutation,
  useCreateMenuAliasMutation,
  useUpdateMenuCategoryMutation,
  useDeleteMenuCategoryMutation,
  useReorderMenuCategoriesMutation,
  useUpdateModifierGroupMutation,
  useDeleteModifierGroupMutation,
  useUpdateModifierMutation,
  useDeleteModifierMutation,
  useSetItemModifierGroupsMutation,
  useDeleteMenuAliasMutation,
  useTestMenuMatchMutation,
  useGetNotificationsQuery
} = api;
